import { ReducerObject } from '../../interfaces/redux';
import { PluginSettings } from '../common';
import { getLang } from './i18n';

export interface Action {
    readonly type: string;
    readonly payload?: any;
}

export interface RootState {
    readonly root: State;
}

export interface State {
    agents: Agent[];
    showIcon: boolean;
    showWindow: boolean;
    transition: string;
    queues: ChatQueue[];
    connected: boolean;
    finished: boolean;
    messages: (IMessage | INotificationMessage)[];
    errorMessage: string;
    settings: PluginSettings;
    pageUrl?: string;
    pageTitle?: string;
    queueId: number | null;
}

export type MessageState = 'sending' | 'sent' | 'failed';

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IMessage extends INotificationMessage {
    id: string;
    direction: 'in' | 'out';
    from: string;
    state?: MessageState;
    attachments?: Attachment[];
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface INotificationMessage {
    date: Date;
    message: string;
}

const ui = getLang(navigator.language);

export const clientReducer: ReducerObject<State> = {
    addAgentToSession(state: State, action: Action): State {
        return {
            ...state,
            agents: state.agents.concat(action.payload),
        };
    },
    close(state: State): State {
        return {
            ...state,
            transition: 'leave',
        };
    },
    closed(state: State): State {
        return {
            ...state,
            showIcon: true,
            showWindow: false,
            transition: '',
        };
    },
    closeConversation(state: State, action: Action): State {
        const { sessionClosedMessage } = action.payload;
        if (!sessionClosedMessage) {
            return {
                ...state,
                finished: true,
            };
        }
        return {
            ...state,
            finished: true,
            messages: state.messages.concat({
                message: sessionClosedMessage,
                date: new Date(),
            }),
        };
    },
    disconnectPlugin(state: State): State {
        return {
            ...state,
            queues: [],
            connected: false,
        };
    },
    frameLoaded(state: State): State {
        return {
            ...state,
            showIcon: true,
        };
    },
    initState(): State {
        const params = new URLSearchParams(window.location.search);
        const pageUrl = params.get('url') || undefined;

        return {
            pageUrl,
            showIcon: false,
            showWindow: false,
            transition: '',
            agents: [],
            queues: [],
            connected: false,
            finished: false,
            messages: [],
            errorMessage: '',
            queueId: null,
            settings: {
                allowedOrigins: '',
                automaticOpen: true,
                automaticOpenTimeout: 15,
                snooze: false,
                snoozeTime: 10,
                welcomeMessage: '',
                noAgentMessage: ui.noAgentsOnline,
                sessionClosedMessage: ui.newSession,
                queueSelectionMessage: ui.selectQueue,
                textColor: '#FFFFFF',
                backgroundColor: '#DC4D28',
                headerLogoUrl: '',
                minimizedPluginText: 'Live\\nchat',
            },
        };
    },
    initWebchat(state: State, action: Action): State {
        const messages = action.payload.messages.map((msg: IMessage) => ({
            id: msg.id,
            date: new Date(msg.date),
            direction: msg.from === action.payload.pluginId ? 'out' : 'in',
            from: msg.from,
            message: msg.message,
        }));
        const queues = sortQueues(action.payload.queues || []);
        const firstQueueId = queues.length === 1 && queues[0].queueId;
        return {
            ...state,
            messages,
            queues,
            agents: action.payload.agents,
            queueId: action.payload.queueId || firstQueueId || null,
            connected: true,
            finished: false,
            settings: { ...state.settings, ...action.payload.settings },
        };
    },
    open(state: State): State {
        return {
            ...state,
            showIcon: false,
            showWindow: true,
            transition: 'enter',
        };
    },
    opened(state: State): State {
        return {
            ...state,
            transition: '',
        };
    },
    updateQueue(state: State, action: Action): State {
        return {
            ...state,
            queueId: action.payload.queueId,
            agents: action.payload.agents,
        };
    },
    updateQueues(state: State, action: Action): State {
        const sortedQueues = sortQueues(action.payload.queues || []);
        const firstQueueId = sortedQueues.length === 1 && sortedQueues[0].queueId;
        const conversationStarted = !!state.messages.filter(isMessage).filter(m => m.direction === 'out').length;
        const queueId = (conversationStarted && state.queueId) || firstQueueId || null;
        return {
            ...state,
            queueId,
            queues: sortedQueues,
        };
    },
    removeAgentFromSession(state: State, action: Action): State {
        return {
            ...state,
            agents: state.agents.filter(agent => agent.agentId !== action.payload),
        };
    },
    receiveMessage(state: State, action: Action): State {
        const messages = state.messages.concat([action.payload]);

        return {
            ...state,
            messages,
        };
    },
    selectQueue(state: State, action: Action): State {
        return {
            ...state,
            queueId: action.payload.id,
        };
    },
    sendMessage(state: State, action: Action): State {
        return this.receiveMessage(state, action);
    },
    serverExit(state: State, action: Action): State {
        return this.disconnectPlugin(state, action);
    },
    setMessageState(state: State, action: Action): State {
        let found = false;
        const message = action.payload.message as IMessage;
        const messages = state.messages.map((m) => {
            if (isMessage(m) && message.id === m.id) {
                found = true;
                let newState = action.payload.state || m.state;
                if (action.payload.state === 'failed' && m.state === 'sent') {
                    newState = m.state;
                }

                return {
                    ...m,
                    state: newState,
                };
            }
            return m;
        });
        if (!found) {
            messages.push(message);
            messages.sort(dateCompare);
        }
        return {
            ...state,
            messages,
        };
    },
    setSiteInfo(state: State, action: Action): State {
        return {
            ...state,
            pageUrl: action.payload.url,
            pageTitle: action.payload.title,
        };
    },
};

function sortQueues(queues: ChatQueue[]) {
    return [...queues.sort((q1, q2) => q1.sort - q2.sort)];
}

function dateCompare(a: INotificationMessage, b: INotificationMessage): number {
    if (a.date < b.date) {
        return -1;
    }
    if (a.date > b.date) {
        return 1;
    }
    return 0;
}

export function isMessage(m: INotificationMessage): m is IMessage {
    return 'id' in m;
}
